import { createContext, DetailedHTMLProps, FC, HTMLAttributes, useContext, useEffect, useMemo, useState } from "react";

import classNames from "classnames";
import uniq from "lodash/uniq";
import uniqueId from "lodash/uniqueId";

type GuideContext = {
  useRegisterGuide: () => { unregisterGuide: () => void; isCurrentGuide: boolean; registerGuide: () => void };
  currentGuide: string | undefined;
};

const guideContext = createContext<GuideContext>({
  useRegisterGuide: () => {
    return {
      unregisterGuide: () => {
        return;
      },
      registerGuide: () => {
        return;
      },
      isCurrentGuide: false,
    };
  },
  currentGuide: "",
});

export const GuideProvider: FC = ({ children }) => {
  const [guides, setGuides] = useState<string[]>([]);

  const useRegisterGuide = useMemo(
    () =>
      function useRegisterGuide() {
        const guideId = useMemo(() => uniqueId("guide_"), []);
        const { currentGuide } = useContext(guideContext);

        const unregisterGuide = useMemo(
          () => () => {
            setGuides((prev) => prev.filter((id) => id !== guideId));
          },
          // eslint-disable-next-line react-hooks/exhaustive-deps
          []
        );

        const registerGuide = useMemo(
          () => () => {
            setGuides((prev) => uniq([...prev, guideId]));
          },
          // eslint-disable-next-line react-hooks/exhaustive-deps
          []
        );

        useEffect(() => {
          registerGuide();
          return unregisterGuide;
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return { unregisterGuide, registerGuide, isCurrentGuide: currentGuide === guideId };
      },
    []
  );

  return (
    <guideContext.Provider value={{ useRegisterGuide, currentGuide: guides[0] }}>{children}</guideContext.Provider>
  );
};

export const Guide: FC<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> & { showGuide: boolean }> = ({
  children,
  showGuide,
  ...props
}) => {
  const { useRegisterGuide } = useContext(guideContext);

  const { unregisterGuide, registerGuide, isCurrentGuide } = useRegisterGuide();

  useEffect(() => {
    if (showGuide === false) {
      unregisterGuide();
    } else {
      registerGuide();
    }
  }, [showGuide, unregisterGuide, registerGuide]);

  return (
    <div {...props} className={classNames(props.className, { guide: isCurrentGuide && showGuide })}>
      {children}
    </div>
  );
};
